import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  ContentChild,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PopoverTriggerDirective } from './popover-trigger.directive';
import { PopoverContentDirective } from './popover-content.directive';

let POPOVER_ID = 0;

@Component({
  selector: 'fx-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopoverComponent implements OnInit, OnDestroy {
  @Input()
  get xPosition() : 'before' | 'after' {
    return this._xPosition;
  }
  set xPosition(position: 'before' | 'after') {
    this._xPosition = position;
    this.setPositionClasses(this.xPosition, this.yPosition);
  }
  private _xPosition: 'before' | 'after' = 'before';

  @Input()
  get yPosition() : 'above' | 'below' {
    return this._yPosition;
  }
  set yPosition(position: 'above' | 'below') {
    this._yPosition = position;
    this.setPositionClasses(this.xPosition, this.yPosition);
  }
  private _yPosition: 'above' | 'below' = 'above';

  @Input('class') 
  set panelClass(classes: string) {
    const previousPanelClass = this._previousPanelClass;

    if (previousPanelClass && previousPanelClass.length) {
      previousPanelClass.split(' ').forEach((className: string) => {
        this.classList[className] = false;
      });
    }

    this._previousPanelClass = classes;

    if (classes && classes.length) {
      classes.split(' ').forEach((className: string) => {
        this.classList[className] = true;
      });
    }
  };
  private _previousPanelClass: string;
  @Input() width: 'lg' | 'md' | 'sm' | string;
  @Input() minWidth: string;
  @Input() maxWidth: string;
  @Input() height: string = 'auto';
  @Input() minHeight: string;
  @Input() maxHeight: string;

  @ViewChild(TemplateRef) templateRef: TemplateRef<any>;
  @ViewChild('panel') panel: ElementRef<HTMLDivElement>;

  @ContentChild(PopoverContentDirective) popoverContent: PopoverContentDirective

  @Output() readonly closed: EventEmitter<any> = new EventEmitter();
  @Output() readonly opened: EventEmitter<any> = new EventEmitter();

  @HostListener('document:click', ['$event.target'])
  _hostClick(targetElement) {
    if (this.panel?.nativeElement) {
      const isInside = this.panel.nativeElement.contains(targetElement);
      if (!isInside) {
        this.setMouseState(false);
      }
    }
  }

  classList: Record<string, boolean> = {};
  isMobileOrTablet = false;
  mouseState$ = new BehaviorSubject<boolean>(false);
  panelId = `fx-popver-panel${++POPOVER_ID}`;
  trigger: PopoverTriggerDirective;

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.setPositionClasses(this.xPosition, this.yPosition);
    if (!this.isMobileOrTablet) {
      switch (this.width) {
        case 'lg':
          this.width = '80vw';
          break;
        case 'md':
          this.width = '60vw';
          break;
        case 'sm':
          this.width = '40vw';
          break;
      }
    }
  }

  ngOnDestroy(): void {
    this.closed.complete();
    this.opened.complete();
    this.mouseState$.complete();
  }

  setPositionClasses(posX, posY) {
    const classes = this.classList;
    classes['fx-popover-after'] = posX === 'after';
    classes['fx-popover-before'] = posX === 'before';
    classes['fx-popover-above'] = posY === 'above';
    classes['fx-popover-below'] = posY === 'below';
  }

  addArrowTranslateX = (offset: number) => {
    const arrowElement: HTMLElement = document.querySelector(`#${this.panelId} .fx-popover-arrow`);
    if (arrowElement && offset) {
      try {
        const style = window.getComputedStyle(arrowElement);
        const matrix = new WebKitCSSMatrix(style.transform);
        const translateX = matrix.m41 + offset;
        const translateY = matrix.m42;
        arrowElement.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`;
      } catch (error) {}
    }
  };

  setMouseState = (visible: boolean) => {
    if (!visible) {
      const activeElement = document.activeElement;
      const panelElement = this.panel?.nativeElement;
      if (panelElement?.contains(activeElement)) {
        return;
      }
    }
    this.mouseState$.next(visible);
  };

  close = () => {
    this.mouseState$.next(false);
    this.closed.emit();
  };

}
